import { flatMap } from 'lodash';
import { createSelector, createSelectorCreator, defaultMemoize } from 'reselect';

import { VIZ_SELECTORS } from '../common/constants';
import { safeParseHTML } from '@mode/shared/util-js';
import { AppState } from '@mode/legacy/types-app-state';
import { tokenFromVizID } from '@mode/charts/domain';

function elementsEqual(a: Element[], b: Element[]) {
  if (a && b) {
    return a === b || (a.length === b.length && a.every((e, i) => e.outerHTML === b[i].outerHTML));
  }

  return false;
}

const createElementsSelector = createSelectorCreator(defaultMemoize, elementsEqual as any);

// NOTE: Do not use jquery in here if possible. If you do, you must check for XSS issues in a report
// like https://modeanalytics.com/modeqa/reports/6cd23d12bed1
export namespace ReportLayoutSelectors {
  export const parsedLayout = createSelector(
    (state: AppState) => state.client.selectedReport.renderedReportLayout,
    (layout) => safeParseHTML(layout).body
  );

  export const vizElementsInLayout = createElementsSelector(parsedLayout, (bodyElements) =>
    flatMap(bodyElements.map((e) => Array.from(e.querySelectorAll(VIZ_SELECTORS))))
  );

  export const chartTokensInLayout = createElementsSelector(vizElementsInLayout, (visualElements) =>
    visualElements.filter((e) => e.tagName === 'MODE-CHART').map((e) => tokenFromVizID(e.id))
  );

  export const tableTokensInLayout = createElementsSelector(vizElementsInLayout, (visualElements) =>
    visualElements.filter((e) => e.tagName === 'MODE-TABLE').map((e) => tokenFromVizID(e.id))
  );

  export const hasScriptTag = createElementsSelector(parsedLayout, (bodyElements) => {
    const elements = flatMap(bodyElements);
    const scriptFlag = elements.some((e) =>
      e.nodeName === 'SCRIPT' || Array.from(e.querySelectorAll('SCRIPT')).length > 0 ? true : false
    );
    return scriptFlag;
  });

  export const hasVizElementsInLayout = createElementsSelector(vizElementsInLayout, (elements) => elements.length > 0);
}
